import React, { useRef, useEffect, useState } from "react";
import { api } from '../services/api';
import Loader from './Loader';
import DiaryUpsell from "./DiaryUpsell";
import PainDiaryHeader from "./PainDiaryHeader";
import PainTimelineChart from "./PainTimelineChart";
import SurgeryChart from "./SurgeryChart";
import ActivityLevelChart from "./ActivityLevelChart";
import AveragesChart from "./AveragesChart";
import { helpers } from '../services/helpers';
import { useNavigate, useParams } from 'react-router-dom';
import PainDiaryEmptyState from "./PainDiaryEmptyState";
import PainDiaryProfile from "./PainDiaryProfile";

const colorPalette = [
  '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
  '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#8dd3c7',
  '#ffffb3', '#bebada', '#fb8072', '#80b1d3', '#fdb462',
  '#b3de69', '#fccde5', '#d9d9d9', '#bc80bd', '#ccebc5'
]

function PainDiary({
  user,
  toast,
  openLoginPrompt,
  showLoginPrompt,
  showSignupModal,
  closeLoginPrompt,
  showUsernamePrompt
}) {
  const { id } = useParams();
  const [configLoaded, setConfigLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [diary, setDiary] = useState({
    attributes: [{ name: "" }],
    public: true,
    custom: false
  })
  const [surgeries, setSurgeries] = useState(null)
  const [steps, setSteps] = useState(["packages", "privacy"])
  const timelineRef = useRef(null);
  const averagesRef = useRef(null);
  const activityLevelRef = useRef(null);
  const surgeryRef = useRef(null)
  const headerRef = useRef();
  const navigate = useNavigate();


  const getColorMapping = (attributes) => {

    const colorMap = new Map();

    colorMap.set('Surgery', colorPalette[0]);

    attributes.forEach((attr, index) => {
      colorMap.set(attr.name, colorPalette[(index + 1) % colorPalette.length]);
    });

    colorMap.set('Activity Level', colorPalette[attributes.length + 1] || '#8dd3c7');

    return colorMap;
  };

  const newEntry = () => navigate('/diary/entry');

  const updateCharts = (filteredDiary, filteredSurgeries) => {
    if (!filteredDiary?.entries?.length) return;

    const colorMap = getColorMapping(filteredDiary.attributes);

    if (timelineRef.current && typeof timelineRef.current.update === 'function') {
      try {
        const newDiary = {
          ...filteredDiary,
          entries: filteredDiary.entries.filter(entry => entry.activityLevel || entry.activityLevel === 0)
        }
        const datesFound = []
        filteredDiary.entries.forEach(entry => {
          datesFound.push(new Date(entry.createdAt))
        })
        filteredSurgeries.forEach(surgery => {
          datesFound.push(new Date(surgery.sharedDate))
        })
        if (datesFound.length === 0) {
          return
        }
        datesFound.sort((a, b) => a - b)
        let minDate = datesFound[0]
        let maxDate = datesFound[datesFound.length - 1]
        timelineRef.current.update(filteredDiary, { colorMap }, minDate, maxDate);
        averagesRef.current.update(filteredDiary, { colorMap })
        activityLevelRef.current.update(newDiary, { colorMap }, minDate, maxDate)
        if (surgeryRef?.current) {
          surgeryRef.current.update(filteredSurgeries, minDate, maxDate)
        }
      } catch (err) {
        console.warn("Timeline update failed:", err);
      }
    }
  };

  const loadData = () => {
    if (user || id) {
      setIsLoading(true);
      setConfigLoaded(false);
      let userID = 0;
      if (id) {
        userID = id
      } else {
        userID = user.id
      }
      Promise.all([
        api.getDiary(userID),
        api.getReviews(null, userID)
      ])
        .then(results => {
          if (results[0]?.id) {
            setDiary(results[0])
            setSteps(["custom package", "privacy"])
          }
          const surgeriesForDiary = []
          results[1].sort(
            (a, b) => new Date(b.surgeryDate) - new Date(a.surgeryDate)
          ).forEach((surgery, surgeryIndex) => {
            let displayName = helpers.surgeryNameDisplay(surgery)
            surgeriesForDiary.push({
              ...surgery,
              isUpdate: false,
              update: null,
              sharedDate: surgery.surgeryDate,
              color: colorPalette[(surgeryIndex + 1) % colorPalette.length],
              depth: -1 - surgeryIndex,
              displayName: displayName,
            })
            surgery.updates.forEach(update => {
              surgeriesForDiary.push({
                ...surgery,
                isUpdate: true,
                update: {
                  ...update
                },
                sharedDate: update.createdAt,
                color: colorPalette[(surgeryIndex + 1) % colorPalette.length],
                depth: -1 - surgeryIndex,
                displayName: displayName
              })
            })
          })
          setSurgeries(
            surgeriesForDiary.sort(
              (a, b) => new Date(a.sharedDate) - new Date(b.sharedDate)
            )
          )
          setConfigLoaded(true);
        })
        .catch(error => {
          console.error('Error loading data:', error);
          setConfigLoaded(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  const clearTimeRangeFilter = () => {
    headerRef.current.handleTimeFilterChange("all")
  }

  useEffect(loadData, [user])

  useEffect(() => {
    if (
      configLoaded &&
      timelineRef.current &&
      averagesRef.current &&
      activityLevelRef.current &&
      ((surgeries.length && surgeryRef.current) || !surgeries.length) &&
      diary?.entries?.length
    ) {
      updateCharts(diary, surgeries);
    }
  }, [configLoaded, timelineRef.current, activityLevelRef.current, surgeryRef.current, averagesRef.current]);

  useEffect(() => {
    if (!user && !showLoginPrompt && !showSignupModal && !showUsernamePrompt && !id) {
      openLoginPrompt()
    }
    if (showLoginPrompt && showSignupModal) {
      closeLoginPrompt()
    }
  })

  if (!user && !id) {
    return <div></div>
  }

  if (isLoading) {
    return (
      <Loader size="large" />
    );
  }

  const canEdit = (user?.id === diary?.patient?.id)

  if (!diary.id || isEditing) {
    return (
      <div className="pain-diary pain-diary-upsell">
        <DiaryUpsell
          toast={toast}
          loadData={loadData}
          setIsEditing={setIsEditing}
          diary={diary}
          setDiary={setDiary}
          steps={steps}
          setSteps={setSteps}
        />
      </div>
    )
  }

  return (
    <div>

      <PainDiaryHeader
        diary={diary}
        surgeries={surgeries}
        updateCharts={updateCharts}
        setIsEditing={setIsEditing}
        ref={headerRef}
        canEdit={canEdit}
      />

      {/* TODO: HIDE PAIN DIARY IN HEADER ON SMALL VIEW WIDTHS */}
      <div className="pain-diary">
        <PainDiaryProfile
          diary={diary}
          canEdit={canEdit}
        />
        {diary?.entries?.length > 0 ? (
          <>
            <AveragesChart
              ref={averagesRef}
              getColorMapping={getColorMapping}
            />
            <PainTimelineChart
              ref={timelineRef}
              getColorMapping={getColorMapping}
              toast={toast}
              loadDiary={loadData}
              canEdit={canEdit}
            />
            <ActivityLevelChart
              ref={activityLevelRef}
              getColorMapping={getColorMapping}
              toast={toast}
              loadDiary={loadData}
              canEdit={canEdit}
            />
            {surgeries?.length > 0 && (
              <SurgeryChart
                ref={surgeryRef}
                clearTimeRangeFilter={clearTimeRangeFilter}
              />
            )}
            <div style={{ marginBottom: '1rem', paddingBottom: '1rem' }}></div>
          </>

        ) : (
          <PainDiaryEmptyState
            canEdit={canEdit}
            newEntry={newEntry}
          />
        )}
      </div>
    </div>
  )
}

export default PainDiary;